/**
 * "Interviews & calendar" on My account: the signed-in user's own availability and next
 * interviews. Shown only to the roles the backend lets read their own bookings
 * (GET /api/users/me/bookings, avrio-backend#256); CHRO, Corporate Executive and
 * Internal Technology never see it. The availability block is further limited to the
 * roles that can set availability.
 *
 * There is deliberately no "Connect calendar" control: a Google or Microsoft connection
 * does not exist yet, and a button for it would claim something false. It is added when
 * the connection is built.
 */

import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Spin, Typography } from 'antd';
import axios from '../../../../../../axiosConfig';
import {
  AvailabilityRule,
  getUserAvailability,
} from '../../../../../calendar/api/availability';
import { UserRole } from '../../../../utils/role';
import { StaffBooking } from '../../types';
import { formatBookingStart, summariseAvailability } from './summary';
import styles from './index.module.css';

const { Text } = Typography;

export const BOOKING_ROLES: ReadonlyArray<UserRole | undefined> = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.HIRING_MANAGER,
  UserRole.RECRUITER,
  UserRole.HR_MANAGER,
];

const AVAILABILITY_ROLES: ReadonlyArray<UserRole | undefined> = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.HIRING_MANAGER,
  UserRole.RECRUITER,
];

const UPCOMING_LIMIT = 5;

type Load<T> =
  { state: 'loading' } | { state: 'error' } | { state: 'ready'; data: T };

const useLoad = <T,>(fetcher: () => Promise<T>, enabled: boolean): Load<T> => {
  const [result, setResult] = useState<Load<T>>({ state: 'loading' });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setResult({ state: 'loading' });
    fetcher()
      .then((data) => {
        if (!cancelled) setResult({ state: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setResult({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  return result;
};

interface Props {
  userId: number;
  role?: UserRole;
  /** The user's chosen display zone from Preferences; absent means each booking's own zone. */
  displayTimezone?: string | null;
}

const InterviewsSection = ({ userId, role, displayTimezone }: Props) => {
  const showAvailability = AVAILABILITY_ROLES.includes(role);
  const availability = useLoad<AvailabilityRule[]>(
    () => getUserAvailability(userId).then((r) => r.rules),
    showAvailability,
  );
  const upcoming = useLoad<StaffBooking[]>(
    () =>
      axios
        .get<StaffBooking[]>('/api/users/me/bookings', {
          params: { from: new Date().toISOString(), limit: UPCOMING_LIMIT },
        })
        .then((r) => r.data),
    true,
  );

  return (
    <div className={styles.blocks}>
      {showAvailability && (
        <div>
          <Text strong>My availability</Text>
          {availability.state === 'loading' && <Spin size="small" />}
          {availability.state === 'error' && (
            <Alert
              type="warning"
              showIcon
              message="Your availability could not be loaded."
            />
          )}
          {availability.state === 'ready' &&
            (availability.data.length === 0 ? (
              <p className={styles.empty}>
                You have not set any availability yet, so candidates cannot book
                time with you.
              </p>
            ) : (
              <ul className={styles.list}>
                {summariseAvailability(availability.data).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ))}
          <p className={styles.link}>
            <Link to="/calendar">Edit availability and calendar link</Link>
          </p>
        </div>
      )}

      <div>
        <Text strong>Upcoming interviews</Text>
        {upcoming.state === 'loading' && <Spin size="small" />}
        {upcoming.state === 'error' && (
          <Alert
            type="warning"
            showIcon
            message="Your upcoming interviews could not be loaded."
          />
        )}
        {upcoming.state === 'ready' &&
          (upcoming.data.length === 0 ? (
            <p className={styles.empty}>No upcoming interviews.</p>
          ) : (
            <>
              <ul className={styles.list}>
                {upcoming.data.map((booking) => (
                  <li key={booking.id}>
                    <span className={styles.when}>
                      {formatBookingStart(booking, displayTimezone)}
                    </span>
                    {' · '}
                    {booking.candidateName}
                    {booking.jobTitle ? `, ${booking.jobTitle}` : ''}
                  </li>
                ))}
              </ul>
              {upcoming.data.length >= UPCOMING_LIMIT && (
                <Text type="secondary" className={styles.hint}>
                  Showing your next {UPCOMING_LIMIT}.
                </Text>
              )}
            </>
          ))}
      </div>
    </div>
  );
};

export default memo(InterviewsSection);
